import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {
  ProcessExecutionType,
  ProcessSingletonTaskSchemaName,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isFlowchartTaskSchema} from 'processes/processes-platform/isFlowchartTaskSchema';
import {
  NodeSettingsTabKeys,
  SidepanelTab,
  STANDARD_END_NODE_ICON,
  STANDARD_ROOT_NODE_ICON,
  STANDARD_ROOT_NODE_NAME,
  TASK_SCHEMA_NODE_ICON,
  EXECUTION_VIEWER_OUTPUT_NODE_NAME,
  FLOWCHART_OUTPUT_SECONDARY_INFO,
  FLOWCHART_TASK_SECONDARY_INFO,
  ProcessTaskDefinitionVersionGraphQL,
  TaskSchemaNodeSection,
  ProcessFlowchartNodeType,
  STANDARD_END_NODE_NAME,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {TaskSchemaNodeSectionType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  FlowchartNode,
  isInteractiveTaskNode,
  TaskDefinitionVersionSpecWithTaskSchema,
  TaskSchemaNodeRowData,
  TaskSchemaNodeSectionData,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import getIconAndLabel from 'registry/field/GetFieldLabelAndIcon';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {ProcessTaskSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {TaskSchemaDetailsTab} from '../flowchart-viz/horizontal-flowchart/nodes/DefaultTaskSchemaNodeDetailsSidepanelContent';
import {getIncrementedNodeName} from '../flowchart-viz/horizontal-flowchart/nodes/nodeUtils';
import {
  ProcessFlowchartAdapter,
  ProcessFlowchartAdditionalPersistedData,
} from '../flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartAdapter';
import {FlowchartConfigWithMetadataAndRootTaskSchema} from '../flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartAdapter';
import {
  DEFAULT_MAX_EDGES,
  FlowchartNodeEditMode,
  NodeCreationContext,
  ProcessFlowchartTaskSchemaNodeAdapter,
  TaskSchemaDisplayOptions,
} from '../flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import {
  FlowchartEditorMode,
  isExecutionMode,
} from '../flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';

interface TaskSchemaSectionMetadata {
  sectionName: string;
  sectionType: TaskSchemaNodeSectionType;
  fields: Array<SchemaField>;
  isFlowchartTaskSchema: boolean;
  shouldSkipSection?: boolean;
}

export const DEFAULT_NODE_SETTINGS_TABS: Array<SidepanelTab> = [
  {
    key: NodeSettingsTabKeys.TASK_SCHEMA_DETAILS,
    label: 'Details',
    content: TaskSchemaDetailsTab,
  },
];

export function getTaskDefinitionFieldClientIdFromSchemaField(field: SchemaField): string {
  // Keep in sync with the backend node adapter's fieldClientIdentifier format.
  // benchling/processes/processes_platform/processes_app/schema_field_to_workflow_field_definition_mapper.py
  return field.apiId;
}

/**
 * Get the field name for a given field ID by searching through the node's sections.
 * This includes dynamically added sections like "Map from Start".
 *
 * @param node - The flowchart node to search within.
 * @param fieldId - The client ID of the field to find.
 * @returns The field name, or null if not found.
 */
export function getFieldNameFromFieldIdForTaskSchemaNode(
  node: FlowchartNode,
  fieldId: string
): string | null {
  assert(isInteractiveTaskNode(node), 'Expected node to be an interactive task node', {node});

  for (const section of node.data.sections) {
    for (const row of section.sectionRows) {
      if (row.clientId === fieldId) {
        return row.fieldName;
      }
    }
  }

  return null;
}

// This function is purely a helper for extracting field icons that need SchemaFieldSelectors
export function maybeGetDefaultSchemaFieldSelectors(field: SchemaField): Array<SchemaFieldSelector> {
  if (field.schemaFieldSelectorId) {
    return [
      {
        // TODO(BNCH-124980): Use the actual name of the dropdown
        name: 'Dropdown',
        id: field.schemaFieldSelectorId,

        // These attributes aren't used
        schemaFieldSelectorOptions: [],
        allSchemaFieldSelectorOptions: [],
      },
    ];
  }

  return [];
}

function getSectionMetadata(
  taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema,
  isFlowchartOutput: boolean
): Array<TaskSchemaSectionMetadata> {
  const taskSchema = taskDefinitionVersion.newProcessTaskDefinitionVersion.taskSchema;
  const isFlowchart = isFlowchartTaskSchema(taskSchema);

  const sections: Array<TaskSchemaSectionMetadata> = [
    {
      sectionName: TaskSchemaNodeSection.INPUTS,
      sectionType: TaskSchemaNodeSectionType.INPUTS,
      fields: taskSchema.fields,
      shouldSkipSection: isFlowchart && isFlowchartOutput,
      isFlowchartTaskSchema: isFlowchart,
    },
  ];

  if (taskSchema.outputSchema != null) {
    sections.push({
      sectionName: TaskSchemaNodeSection.OUTPUTS,
      sectionType: TaskSchemaNodeSectionType.OUTPUTS,
      fields: taskSchema.outputSchema.fields,
      shouldSkipSection: isFlowchart && !isFlowchartOutput,
      isFlowchartTaskSchema: isFlowchart,
    });
  }

  return sections;
}

function createSectionRows(metadata: TaskSchemaSectionMetadata): Array<TaskSchemaNodeRowData> {
  const {fields, sectionName, sectionType, isFlowchartTaskSchema} = metadata;
  return fields.map((field) => {
    const iconAndLabel = getIconAndLabel({
      field,
      schemaFieldSelectors: maybeGetDefaultSchemaFieldSelectors(field),
    });

    const fieldClientId = getTaskDefinitionFieldClientIdFromSchemaField(field);

    return {
      id: uuidv4(),
      clientId: fieldClientId,
      fieldName: field.name,
      fieldIcon: iconAndLabel.icon,
      fieldTypeLabel: iconAndLabel.label,
      sectionName,
      sectionType,
      isFlowchartTask: isFlowchartTaskSchema,
      canBeMappedTo: !field.isSnapshot,
    };
  });
}

function createSections(
  taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema,
  nodeDetailsType: ProcessFlowchartNodeDetailsType
): Array<TaskSchemaNodeSectionData> {
  const isFlowchartOutput = nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS;
  const sectionMetadatas = getSectionMetadata(taskDefinitionVersion, isFlowchartOutput);

  const sections = sectionMetadatas
    .filter((metadata) => !metadata.shouldSkipSection)
    .map((metadata) => ({
      sectionName: metadata.sectionName,
      sectionType: metadata.sectionType,
      sectionRows: createSectionRows(metadata),
    }));

  const shouldAddMapFromStart =
    nodeDetailsType !== ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS &&
    nodeDetailsType !== ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS;

  if (shouldAddMapFromStart) {
    sections.unshift({
      sectionName: TaskSchemaNodeSection.MAP_FROM_START,
      sectionType: TaskSchemaNodeSectionType.MAP_FROM_START,
      sectionRows: [],
    });
  }

  return sections;
}

/**
 * Adapter allowing direct execution tasks to be rendered and configured in the processes editor.
 */
export class DirectExecutionTaskSchemaNodeAdapter extends ProcessFlowchartTaskSchemaNodeAdapter<null, null> {
  executionType: ProcessExecutionType = ProcessExecutionType.DIRECT;
  readonly typeDisplayName: string = 'task';
  readonly editMode = FlowchartNodeEditMode.SINGLE_CLICK;

  override isDuplicable(): boolean {
    return true;
  }

  convertTaskDefinitionVersionGQLToApplicationData(
    taskDefinitionVersionGQL: ProcessTaskDefinitionVersionGraphQL
  ): null {
    return null;
  }
  convertTaskDefinitionVersionSpecToNetworkApplicationData(
    taskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema<null>,
    flowchartConfigId: string
  ): null {
    return null;
  }

  createSections(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema,
    nodeDetailsType: ProcessFlowchartNodeDetailsType
  ): Array<TaskSchemaNodeSectionData> {
    return createSections(taskDefinitionVersion, nodeDetailsType);
  }

  nodeDetailsSidepanelContent: Array<SidepanelTab> = DEFAULT_NODE_SETTINGS_TABS;

  getNodeSubtitle(): string | null {
    return null;
  }

  getNodeName(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<null>,
    _editorMode: FlowchartEditorMode,
    _isFlowchartOutput?: boolean,
    existingNodeNames?: Array<string>
  ): string {
    const taskSchema = taskDefinitionVersion.newProcessTaskDefinitionVersion.taskSchema;

    return getIncrementedNodeName(taskSchema.name, existingNodeNames);
  }

  getNodeIcon(taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema): BenchlingIconName {
    return TASK_SCHEMA_NODE_ICON;
  }

  onCreateNewNode(
    context: NodeCreationContext
  ): Promise<TaskDefinitionVersionSpecWithTaskSchema<null> | null> {
    // TODO: Implement direct execution node creation logic
    return Promise.resolve(null);
  }

  getNewNodeDetails(context: NodeCreationContext): {
    nodeName: string;
    nodeSubtitle: string | null;
    nodeIcon: BenchlingIconName | null;
  } {
    return {
      nodeName: 'New direct task schema',
      nodeSubtitle: 'Executed in a modal or via API',
      nodeIcon: 'table',
    };
  }

  getSingletonTaskSchemaName(
    rootTaskSchema: ProcessTaskSchemaGraphQL
  ): ProcessSingletonTaskSchemaName | null {
    return null;
  }

  override getFieldNameFromFieldId(node: FlowchartNode, fieldId: string): string | null {
    return getFieldNameFromFieldIdForTaskSchemaNode(node, fieldId);
  }

  override getDisplayOptions(): TaskSchemaDisplayOptions {
    return {
      ...super.getDisplayOptions(),
      rightSidepanelNameOverride: (nodeName: string, selectedFieldName: string | null) => {
        if (selectedFieldName != null) {
          return `Edit field mappings: ${selectedFieldName}`;
        } else {
          return `Edit task schema: ${nodeName}`;
        }
      },
    };
  }
}

/**
 * Adapter allowing entry tasks to be rendered and configured in the processes editor.
 */
export class EntryExecutionTaskSchemaNodeAdapter extends ProcessFlowchartTaskSchemaNodeAdapter<null, null> {
  executionType: ProcessExecutionType = ProcessExecutionType.ENTRY;
  readonly typeDisplayName: string = 'task';
  readonly editMode = FlowchartNodeEditMode.SINGLE_CLICK;

  override isDuplicable(): boolean {
    return true;
  }

  convertTaskDefinitionVersionGQLToApplicationData(
    taskDefinitionVersionGQL: ProcessTaskDefinitionVersionGraphQL
  ): null {
    return null;
  }

  convertTaskDefinitionVersionSpecToNetworkApplicationData(
    taskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema<null>,
    flowchartConfigId: string
  ): null {
    return null;
  }

  createSections(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema,
    nodeDetailsType: ProcessFlowchartNodeDetailsType
  ): Array<TaskSchemaNodeSectionData> {
    return createSections(taskDefinitionVersion, nodeDetailsType);
  }

  nodeDetailsSidepanelContent: Array<SidepanelTab> = DEFAULT_NODE_SETTINGS_TABS;

  getNodeSubtitle(): string | null {
    return null;
  }

  getNodeName(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<null>,
    _editorMode: FlowchartEditorMode,
    _isFlowchartOutput?: boolean,
    existingNodeNames?: Array<string>
  ): string {
    const taskSchema = taskDefinitionVersion.newProcessTaskDefinitionVersion.taskSchema;

    return getIncrementedNodeName(taskSchema.name, existingNodeNames);
  }

  getNodeIcon(taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema): BenchlingIconName {
    return TASK_SCHEMA_NODE_ICON;
  }

  onCreateNewNode(
    context: NodeCreationContext
  ): Promise<TaskDefinitionVersionSpecWithTaskSchema<null> | null> {
    // TODO: Implement entry execution node creation logic
    return Promise.resolve(null);
  }

  getNewNodeDetails(context: NodeCreationContext): {
    nodeName: string;
    nodeSubtitle: string | null;
    nodeIcon: BenchlingIconName | null;
  } {
    return {
      nodeName: 'New entry task schema',
      nodeSubtitle: 'Executed in a notebook entry',
      nodeIcon: 'file',
    };
  }

  getSingletonTaskSchemaName(
    rootTaskSchema: ProcessTaskSchemaGraphQL
  ): ProcessSingletonTaskSchemaName | null {
    return null;
  }

  override getFieldNameFromFieldId(node: FlowchartNode, fieldId: string): string | null {
    return getFieldNameFromFieldIdForTaskSchemaNode(node, fieldId);
  }

  override getDisplayOptions(): TaskSchemaDisplayOptions {
    return {
      ...super.getDisplayOptions(),
      rightSidepanelNameOverride: (nodeName: string, selectedFieldName: string | null) => {
        if (selectedFieldName != null) {
          return `Edit field mappings: ${selectedFieldName}`;
        } else {
          return `Edit task schema: ${nodeName}`;
        }
      },
    };
  }
}

/**
 * Adapter allowing flowchart tasks to be rendered and configured in the processes editor.
 */
export class FlowchartExecutionTaskSchemaNodeAdapter extends ProcessFlowchartAdapter<null, null> {
  static override readonly AUTOSAVE_FRIENDLY_NAME: string = 'Flowchart';
  executionType: ProcessExecutionType = ProcessExecutionType.FLOWCHART;
  readonly typeDisplayName: string = 'flowchart';
  readonly editMode = FlowchartNodeEditMode.SINGLE_CLICK;

  convertTaskDefinitionVersionGQLToApplicationData(
    taskDefinitionVersionGQL: ProcessTaskDefinitionVersionGraphQL
  ): null {
    return null;
  }

  convertTaskDefinitionVersionSpecToNetworkApplicationData(
    taskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema<null>,
    flowchartConfigId: string
  ): null {
    return null;
  }

  createSections(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema,
    nodeDetailsType: ProcessFlowchartNodeDetailsType
  ): Array<TaskSchemaNodeSectionData> {
    return createSections(taskDefinitionVersion, nodeDetailsType);
  }

  // The flowchart details are shown in the left sidepanel
  nodeDetailsSidepanelContent: Array<SidepanelTab> = [];

  getNodeSubtitle(
    _taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema,
    editorMode: FlowchartEditorMode,
    isFlowchartOutput?: boolean
  ): string | null {
    if (isExecutionMode(editorMode)) {
      return null;
    }
    return isFlowchartOutput === true ? FLOWCHART_OUTPUT_SECONDARY_INFO : FLOWCHART_TASK_SECONDARY_INFO;
  }

  getNodeName(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<null>,
    editorMode: FlowchartEditorMode,
    isFlowchartOutput?: boolean
  ): string {
    if (editorMode === FlowchartEditorMode.EXECUTION_VIEWER) {
      if (isFlowchartOutput === true) {
        return EXECUTION_VIEWER_OUTPUT_NODE_NAME;
      }
      return taskDefinitionVersion.newProcessTaskDefinitionVersion.taskSchema.name;
    }
    return isFlowchartOutput ? STANDARD_END_NODE_NAME : STANDARD_ROOT_NODE_NAME;
  }

  getNodeIcon(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema,
    isFlowchartOutput?: boolean
  ): BenchlingIconName {
    if (isFlowchartOutput === true) {
      return STANDARD_END_NODE_ICON;
    } else {
      return STANDARD_ROOT_NODE_ICON;
    }
  }

  onCreateNewNode(
    context: NodeCreationContext
  ): Promise<TaskDefinitionVersionSpecWithTaskSchema<null> | null> {
    assert(false, 'Flowchart nodes should not be created');
  }

  getNewNodeDetails(context: NodeCreationContext): {
    nodeName: string;
    nodeSubtitle: string | null;
    nodeIcon: BenchlingIconName | null;
  } {
    assert(false, 'Flowchart nodes should not be created');
  }

  getSingletonTaskSchemaName(
    rootTaskSchema: ProcessTaskSchemaGraphQL
  ): ProcessSingletonTaskSchemaName | null {
    return null;
  }

  onSaveStart(flowchartConfig: FlowchartConfigWithMetadataAndRootTaskSchema): void {
    // No additional actions needed before saving
  }

  onSaveComplete(
    persistedFlowchartConfig: FlowchartConfigWithMetadataAndRootTaskSchema,
    additionalPersistedData: ProcessFlowchartAdditionalPersistedData
  ): Promise<FlowchartConfigWithMetadataAndRootTaskSchema> | null {
    return null;
  }

  override getFieldNameFromFieldId(node: FlowchartNode, fieldId: string): string | null {
    return getFieldNameFromFieldIdForTaskSchemaNode(node, fieldId);
  }

  override getDisplayOptions(): TaskSchemaDisplayOptions {
    return {
      ...super.getDisplayOptions(),
      rightSidepanelNameOverride: (nodeName: string, selectedFieldName: string | null) => {
        if (selectedFieldName != null) {
          return `Edit field mappings: ${selectedFieldName}`;
        } else {
          return `Edit flowchart ${nodeName === STANDARD_ROOT_NODE_NAME ? 'inputs' : 'outputs'}`;
        }
      },
    };
  }

  override isDeletable(): boolean {
    return false;
  }

  override maxIncomingEdges(isFlowchartOutput?: boolean): number {
    if (isFlowchartOutput) {
      return DEFAULT_MAX_EDGES;
    } else {
      return 0;
    }
  }

  override maxOutgoingEdges(isFlowchartOutput?: boolean): number {
    if (isFlowchartOutput) {
      return 0;
    } else {
      return DEFAULT_MAX_EDGES;
    }
  }

  override blockedAdjacentNodeTypes(): Array<ProcessFlowchartNodeType> {
    return [ProcessFlowchartNodeType.STOP_NODE];
  }
}
